import React, { useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import {
  Button,
  Container,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { CreateGameConfig, Game } from '@par/shared/types/game';
import { Profile } from '@par/shared/types/player';
import { TournamentLobbyTable } from './TournamentLobbyTable';
import {
  CustomMessage,
  CustomMessageModal,
} from '../../modals/CustomMessageModal';
// import './styles.css';
import { ImageDisplay } from '../../dialog/ImageDisplay';
import { fetchGet } from '../../api/common';
import { CreateTourneyGameDialog } from '../../dialog/CreateTourneyGameDialog';
import { CreateTestTourneyGameDialog } from '../../dialog/CreateTestTourneyGameDialog';
import { BsTwitch } from 'react-icons/bs';
import { FaDiscord } from 'react-icons/fa';
import { Mode } from '@par/shared/common/types';

type PropTypes = {
  handleJoinGameClick: (gameId: string, mode: Mode) => void;
  handleSpectateClick: (gameId: string) => void;
  lobbyGames?: Game[];
  lobbyGamesHistory?: Game[];
  onTourneyGameCreate: (gameConfig: CreateGameConfig) => void;
  profile: Profile;
  socket: Socket;
};

export const TournamentLobby = ({
  handleJoinGameClick,
  handleSpectateClick,
  lobbyGames,
  lobbyGamesHistory,
  onTourneyGameCreate,
  profile,
  socket,
}: PropTypes) => {
  const [tourneyLobbyGames, setTourneyLobbyGames] = useState<Game[]>();
  const [openTourneyGameDialog, setOpenTourneyGameDialog] = useState(false);
  const [openTestTourneyGameDialog, setOpenTestTourneyGameDialog] =
    useState(false);
  const [customMessage, setCustomMessage] = useState<CustomMessage>({
    openModal: false,
  });
  const [loadingGames, setLoadingGames] = useState(true);
  const [openTourneyImage, setOpenTourneyImage] = useState(false);
  const [tourneyImgSource, setTourneyImgSource] = useState('');

  useEffect(() => {
    if (!lobbyGames || !lobbyGamesHistory) return;
    const combinedGames = lobbyGamesHistory
      ? lobbyGamesHistory.concat(lobbyGames)
      : lobbyGames;
    setTourneyLobbyGames(
      combinedGames?.sort((a, b) => b.createdAt - a.createdAt)
    );
    setLoadingGames(false);
  }, [lobbyGames, lobbyGamesHistory]);

  const handleCustomMessageSubmit = () => {
    setCustomMessage({ openModal: false });
  };

  const handleRemoveGameClick = (game: Game) => {
    socket.emit('deleteGame', {
      profileId: profile.id,
      gameId: game.gameId,
    });
  };

  const handleLeaveGameClick = (gameId: string, profileId: string) => {
    socket.emit('leaveGame', {
      gameId,
      profileId,
    });
  };

  const handleForceStartClick = (game: Game) => {
    socket.emit('forceStartSitAndGo', {
      gameId: game.gameId,
      profileId: profile.id,
    });
  };

  const handleDisplayTourneyFormat = () => {
    setTourneyImgSource(
      'https://res.cloudinary.com/dqlqwdqs9/image/upload/v1695537527/beta-tourney-6-flow_pmkxic.png'
    );
    setOpenTourneyImage(true);
  };

  const handleDisplayTourneyRules = () => {
    setTourneyImgSource(
      'https://res.cloudinary.com/dqlqwdqs9/image/upload/v1682897962/Tourney_Rules_2_givbij.png'
    );
    setOpenTourneyImage(true);
  };

  const handleCreateTourneyGame = () => {
    setOpenTourneyGameDialog(true);
  };

  const handleCreateTestTourneyGame = () => {
    setOpenTestTourneyGameDialog(true);
  };

  const modals = (
    <>
      <CreateTourneyGameDialog
        close={() => setOpenTourneyGameDialog(false)}
        onTourneyGameCreate={onTourneyGameCreate}
        open={openTourneyGameDialog}
      ></CreateTourneyGameDialog>
      <CreateTestTourneyGameDialog
        close={() => setOpenTestTourneyGameDialog(false)}
        onTourneyGameCreate={onTourneyGameCreate}
        open={openTestTourneyGameDialog}
      ></CreateTestTourneyGameDialog>
      <CustomMessageModal
        customMessage={customMessage}
        handleCustomMessageSubmit={handleCustomMessageSubmit}
        setCustomMessage={setCustomMessage}
      ></CustomMessageModal>
      <ImageDisplay
        closeImageDisplay={() => setOpenTourneyImage(false)}
        imgSource={tourneyImgSource}
        openImageDisplay={openTourneyImage}
      ></ImageDisplay>
    </>
  );

  return (
    <>
      {modals}
      <Container sx={{ paddingBottom: 20 }}>
        {!loadingGames ? (
          <TournamentLobbyTable
            handleForceStartClick={handleForceStartClick}
            handleJoinGameClick={handleJoinGameClick}
            handleLeaveGameClick={handleLeaveGameClick}
            handleRemoveGameClick={handleRemoveGameClick}
            handleSpectateClick={handleSpectateClick}
            lobbyGames={tourneyLobbyGames}
            profile={profile}
          />
        ) : (
          <Stack>
            <Typography variant="h6led" textAlign={'center'} mt={5} mb={5}>
              Loading tournament lobby...
            </Typography>
            <LinearProgress />
          </Stack>
        )}
      </Container>
    </>
  );
};
